import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrToStr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HtmlEditor } from "./HtmlEditor";
import { ImageUploader, GalleryUploader } from "./ImageUploader";
import { F } from "./F";

export const TYPES = ["dham", "temple", "yatra", "hotel", "dharamshala", "package"];
const toList = (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v) || [];
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function buildPayload(f) {
  return {
    type: f.type || "package", name: f.name, state: f.state || null, city: f.city || null,
    short_desc: f.short_desc || "", description: f.description || "", image: f.image || "", gallery: f.gallery || [],
    price: num(f.price), rating: num(f.rating), duration_days: num(f.duration_days), altitude_m: num(f.altitude_m),
    best_season: f.best_season || null, darshan_timings: f.darshan_timings || null, nearest_airport: f.nearest_airport || null,
    is_bookable: !!f.is_bookable, featured: !!f.featured, published: f.published !== false,
    tags: toList(f.tags), inclusions: toList(f.inclusions), highlights: toList(f.highlights), facilities: toList(f.facilities),
    seo_title: f.seo_title || null, seo_desc: f.seo_desc || null, address: f.address || null,
    check_in_time: f.check_in_time || null, check_out_time: f.check_out_time || null, policy: f.policy || null,
  };
}

export function EntityFormDialog({ editing, onClose }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(editing || {}); }, [editing]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const inp = (k) => (e) => set(k)(e.target.value);
  const isStay = f.type === "hotel" || f.type === "dharamshala";

  const save = async () => {
    const payload = buildPayload(f);
    if (!payload.name) return toast.error("Name is required");
    try {
      if (editing?.id) await api.put(`/admin/entities/${editing.id}`, payload);
      else await api.post("/admin/entities", payload);
      toast.success("Saved");
      onClose();
    } catch (err) { toast.error(apiErrToStr(err)); }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-offwhite">
      <DialogHeader><DialogTitle className="font-display text-xl text-indigo-deep">{editing?.id ? "Edit" : "New"} listing</DialogTitle></DialogHeader>
      <Tabs defaultValue="basic">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="ftab-basic" value="basic">Basic</TabsTrigger>
          <TabsTrigger data-testid="ftab-content" value="content">Description</TabsTrigger>
          <TabsTrigger data-testid="ftab-gallery" value="gallery">Photos</TabsTrigger>
          {isStay && <TabsTrigger data-testid="ftab-hotel" value="hotel">Hotel details</TabsTrigger>}
          <TabsTrigger data-testid="ftab-seo" value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-3 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Type">
              <Select value={f.type || "package"} onValueChange={set("type")}>
                <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Name"><Input data-testid="form-name" value={f.name || ""} onChange={inp("name")} /></F>
            <F label="State"><Input value={f.state || ""} onChange={inp("state")} /></F>
            <F label="City"><Input value={f.city || ""} onChange={inp("city")} /></F>
            <F label="Price (INR)"><Input type="number" value={f.price ?? ""} onChange={inp("price")} /></F>
            <F label="Rating"><Input type="number" step="0.1" max="5" value={f.rating ?? ""} onChange={inp("rating")} /></F>
            <F label="Duration (days)"><Input type="number" value={f.duration_days ?? ""} onChange={inp("duration_days")} /></F>
            <F label="Altitude (m)"><Input type="number" value={f.altitude_m ?? ""} onChange={inp("altitude_m")} /></F>
            <F label="Best season"><Input value={f.best_season || ""} onChange={inp("best_season")} /></F>
            <F label="Darshan timings"><Input value={f.darshan_timings || ""} onChange={inp("darshan_timings")} /></F>
            <F label="Nearest airport"><Input value={f.nearest_airport || ""} onChange={inp("nearest_airport")} /></F>
          </div>
          <F label="Address"><Input data-testid="form-address" value={f.address || ""} onChange={inp("address")} placeholder="Street, landmark, pin code" /></F>
          <F label="Tags (comma separated)"><Input value={Array.isArray(f.tags) ? f.tags.join(", ") : (f.tags || "")} onChange={inp("tags")} /></F>
          <F label="Inclusions (comma separated)"><Input value={Array.isArray(f.inclusions) ? f.inclusions.join(", ") : (f.inclusions || "")} onChange={inp("inclusions")} /></F>
          <F label="Highlights (comma separated)"><Input value={Array.isArray(f.highlights) ? f.highlights.join(", ") : (f.highlights || "")} onChange={inp("highlights")} /></F>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.is_bookable} onChange={(e) => set("is_bookable")(e.target.checked)} /> Bookable</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured")(e.target.checked)} /> Featured</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.published !== false} onChange={(e) => set("published")(e.target.checked)} /> Published</label>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-3 mt-3">
          <F label="Short description (cards & meta fallback)"><Input data-testid="form-short-desc" value={f.short_desc || ""} onChange={inp("short_desc")} /></F>
          <F label="Full description (rich text)"><HtmlEditor testId="form-description" value={f.description} onChange={set("description")} /></F>
        </TabsContent>

        <TabsContent value="gallery" className="space-y-3 mt-3">
          <F label="Cover image"><ImageUploader testId="form-image" value={f.image} onChange={set("image")} /></F>
          <F label="Photo gallery"><GalleryUploader images={f.gallery || []} onChange={set("gallery")} /></F>
        </TabsContent>

        {isStay && (
          <TabsContent value="hotel" className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="Check-in time"><Input data-testid="form-checkin" value={f.check_in_time || ""} onChange={inp("check_in_time")} placeholder="12:00 PM" /></F>
              <F label="Check-out time"><Input data-testid="form-checkout" value={f.check_out_time || ""} onChange={inp("check_out_time")} placeholder="10:00 AM" /></F>
            </div>
            <F label="Facilities (comma separated)"><Textarea data-testid="form-facilities" rows={2} value={Array.isArray(f.facilities) ? f.facilities.join(", ") : (f.facilities || "")} onChange={inp("facilities")} placeholder="Free Wi-Fi, Hot water, Parking" /></F>
            <F label="Hotel policy (rich text)"><HtmlEditor testId="form-policy" value={f.policy} onChange={set("policy")} /></F>
          </TabsContent>
        )}

        <TabsContent value="seo" className="space-y-3 mt-3">
          <F label="SEO title"><Input data-testid="form-seo-title" value={f.seo_title || ""} onChange={inp("seo_title")} maxLength={70} /></F>
          <div className="text-[11px] text-indigo-deep/50 -mt-2">{(f.seo_title || "").length}/70</div>
          <F label="Meta description"><Textarea data-testid="form-seo-desc" rows={3} value={f.seo_desc || ""} onChange={inp("seo_desc")} maxLength={160} /></F>
          <div className="text-[11px] text-indigo-deep/50 -mt-2">{(f.seo_desc || "").length}/160</div>
        </TabsContent>
      </Tabs>
      <Button data-testid="form-save" onClick={save} className="w-full bg-indigo-deep text-white mt-2">Save</Button>
    </DialogContent>
  );
}
